/**
 * packaging.ts - Parametry pakowania i transportu
 * PlexiSystem Engineering Module
 */

export const PACKAGING = {
  // Współczynniki strukturalne dla kartonów
  k_struct: {
    standard: [0.22, 0.37],    // Standardowe pudła
    reinforced: [0.38, 0.45],  // Wzmocnione
    heavy_duty: [0.46, 0.55]   // Ciężkie
  },
  
  // Edge Crush Test - wytrzymałość tektury [kN/m]
  ECT_kN_per_m: {
    light: [2, 4],      // Lekkie produkty
    medium: [4, 6],     // Średnie
    heavy: [6, 10],     // Ciężkie
    extra_heavy: [10, 15] // Bardzo ciężkie
  },
  
  // Luzy i odstępy [mm]
  clearance_mm: {
    standard: [40, 60],     // Standardowy luz
    fragile: [60, 100],     // Delikatne produkty
    precision: [20, 40]     // Precyzyjne dopasowanie
  },
  
  // Pianki ochronne
  foam_PE_density_kg_m3: {
    soft: [20, 30],         // Miękka
    medium: [30, 45],       // Średnia
    hard: [45, 60]          // Twarda
  },
  
  // Współczynniki bezpieczeństwa
  safety_factor: {
    local: 2.0,             // Transport lokalny
    domestic: 3.0,          // Krajowy
    intl: 4.0,              // Międzynarodowy
    air: 5.0                // Lotniczy
  },
  
  // Standardowe wymiary palet [mm]
  pallet_dimensions: {
    euro: [800, 1200, 144],
    industrial: [1000, 1200, 144],
    half_euro: [600, 800, 144],
    quarter_euro: [400, 600, 144]
  },
  
  // Maksymalne wysokości stosów [mm]
  max_stack_height: {
    manual: 1400,           // Ręczne układanie
    forklift: 1800,         // Wózek widłowy
    container: 2400         // Kontener
  },
  
  // Koszty orientacyjne [PLN]
  costs_pln: {
    carton_per_m2: [3, 8],
    foam_per_m3: [150, 300],
    bubble_wrap_per_m2: [2, 5],
    stretch_film_per_kg: [8, 12],
    pallet_euro: [35, 50],
    corner_protector: [2, 5]
  }
};

/**
 * Oblicza wymagane BCT (Box Compression Test)
 */
export function calculateBCT(
  productWeight_kg: number,
  stackLayers: number,
  route: 'local' | 'domestic' | 'intl' | 'air'
): number {
  const safetyFactor = PACKAGING.safety_factor[route];
  const totalWeight = productWeight_kg * (stackLayers - 1); // Ciężar nad pudłem
  const requiredBCT = totalWeight * 9.81 * safetyFactor; // N
  
  return Math.ceil(requiredBCT);
}

/**
 * Oblicza wymiary kartonu na podstawie wymiarów produktu
 */
export function calculateCartonDimensions(
  productDims_mm: [number, number, number],
  fragile: boolean = false
): {
  internal_mm: [number, number, number];
  external_mm: [number, number, number];
  clearance_mm: number;
} {
  const clearanceRange = fragile ? PACKAGING.clearance_mm.fragile : PACKAGING.clearance_mm.standard;
  const clearance = (clearanceRange[0] + clearanceRange[1]) / 2;
  
  const internal_mm: [number, number, number] = [
    productDims_mm[0] + 2 * clearance,
    productDims_mm[1] + 2 * clearance,
    productDims_mm[2] + 2 * clearance
  ];
  
  // Grubość ścianki kartonu ~5mm
  const wallThickness = 5;
  const external_mm: [number, number, number] = [
    internal_mm[0] + 2 * wallThickness,
    internal_mm[1] + 2 * wallThickness,
    internal_mm[2] + 2 * wallThickness
  ];
  
  return {
    internal_mm,
    external_mm,
    clearance_mm: clearance
  };
}

/**
 * Oblicza ilość produktów na palecie
 */
export function calculatePalletization(
  cartonDims_mm: [number, number, number],
  palletType: 'euro' | 'industrial' = 'euro',
  maxHeight_mm: number = PACKAGING.max_stack_height.forklift
): {
  cartonsPerLayer: number;
  layers: number;
  totalCartons: number;
  efficiency: number;
  arrangement: string;
} {
  const pallet = PACKAGING.pallet_dimensions[palletType];
  const [palletWidth, palletLength] = pallet;
  
  // Próbuj różne układy
  const arrangements = [
    { name: 'lengthwise', w: cartonDims_mm[0], l: cartonDims_mm[1] },
    { name: 'widthwise', w: cartonDims_mm[1], l: cartonDims_mm[0] },
    { name: 'mixed', w: cartonDims_mm[0], l: cartonDims_mm[1] } // Do optymalizacji
  ];
  
  let bestArrangement = {
    cartonsPerLayer: 0,
    arrangement: ''
  };
  
  for (const arr of arrangements) {
    const cartonsWidth = Math.floor(palletWidth / arr.w);
    const cartonsLength = Math.floor(palletLength / arr.l);
    const total = cartonsWidth * cartonsLength;
    
    if (total > bestArrangement.cartonsPerLayer) {
      bestArrangement.cartonsPerLayer = total;
      bestArrangement.arrangement = `${cartonsWidth}×${cartonsLength} ${arr.name}`;
    }
  }
  
  const layers = Math.floor((maxHeight_mm - pallet[2]) / cartonDims_mm[2]);
  const totalCartons = bestArrangement.cartonsPerLayer * layers;
  
  // Efektywność wykorzystania powierzchni palety
  const usedArea = bestArrangement.cartonsPerLayer * cartonDims_mm[0] * cartonDims_mm[1];
  const palletArea = palletWidth * palletLength;
  const efficiency = (usedArea / palletArea) * 100;
  
  return {
    cartonsPerLayer: bestArrangement.cartonsPerLayer,
    layers,
    totalCartons,
    efficiency: Math.round(efficiency),
    arrangement: bestArrangement.arrangement
  };
}

/**
 * Oblicza wymaganą wytrzymałość tektury ECT
 */
export function calculateRequiredECT(
  BCT_N: number,
  cartonPerimeter_mm: number,
  cartonHeight_mm: number,
  structuralCoeff: number = 0.3
): number {
  // McKee formula: BCT = k * ECT * sqrt(h * P)
  // ECT = BCT / (k * sqrt(h * P))
  
  const h_m = cartonHeight_mm / 1000;
  const P_m = cartonPerimeter_mm / 1000;
  
  const ECT_N_per_m = BCT_N / (structuralCoeff * Math.sqrt(h_m * P_m));
  const ECT_kN_per_m = ECT_N_per_m / 1000;
  
  return Math.ceil(ECT_kN_per_m * 10) / 10; // Zaokrąglij do 0.1
}

/**
 * Dobiera typ pianki ochronnej
 */
export function selectFoamType(
  productWeight_kg: number,
  fragility: 'low' | 'medium' | 'high'
): {
  density_kg_m3: number;
  thickness_mm: number;
  type: string;
} {
  let density: number;
  let thickness: number;
  let type: string;
  
  if (fragility === 'high') {
    density = PACKAGING.foam_PE_density_kg_m3.hard[0];
    thickness = Math.max(30, productWeight_kg * 2);
    type = 'PE hard foam';
  } else if (fragility === 'medium') {
    density = PACKAGING.foam_PE_density_kg_m3.medium[0];
    thickness = Math.max(20, productWeight_kg * 1.5);
    type = 'PE medium foam';
  } else {
    density = PACKAGING.foam_PE_density_kg_m3.soft[0];
    thickness = Math.max(15, productWeight_kg);
    type = 'PE soft foam';
  }
  
  return {
    density_kg_m3: density,
    thickness_mm: Math.min(thickness, 100), // Max 100mm
    type
  };
}

/**
 * Szacuje koszt opakowania
 */
export function estimatePackagingCost(
  cartonArea_m2: number,
  foamVolume_m3: number,
  usePallet: boolean = false,
  cornerProtectors: number = 0
): {
  carton: number;
  foam: number;
  pallet: number;
  corners: number;
  total: number;
} {
  const costs = PACKAGING.costs_pln;
  
  const cartonCost = cartonArea_m2 * ((costs.carton_per_m2[0] + costs.carton_per_m2[1]) / 2);
  const foamCost = foamVolume_m3 * ((costs.foam_per_m3[0] + costs.foam_per_m3[1]) / 2);
  const palletCost = usePallet ? ((costs.pallet_euro[0] + costs.pallet_euro[1]) / 2) : 0;
  const cornersCost = cornerProtectors * ((costs.corner_protector[0] + costs.corner_protector[1]) / 2);
  
  return {
    carton: Math.round(cartonCost * 100) / 100,
    foam: Math.round(foamCost * 100) / 100,
    pallet: Math.round(palletCost * 100) / 100,
    corners: Math.round(cornersCost * 100) / 100,
    total: Math.round((cartonCost + foamCost + palletCost + cornersCost) * 100) / 100
  };
}
